// src/component/Events/CardSlider.jsx

import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PlaceholderCard from './PlaceholderCard';
import PropTypes from 'prop-types';
import { truncateText } from './truncateText';
import FavoriteButton from './FavoriteButton';
import { useDispatch, useSelector } from 'react-redux';
import { getWishlist } from '../../redux/auth/action/fetchWishlist';

/**
 * CardSlider Component - Professional Compact Layout
 */
const CardSlider = ({
  loading = false,
  data = [],
  isPurchaseHistory = false,
  onCardClick = null,
}) => {
  const sliderRef = useRef(null);
  const dispatch = useDispatch();
  const { wishlist } = useSelector(state => state.wishlist);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const handleWheel = (e) => {
      const { scrollWidth, clientWidth, scrollLeft } = slider;
      const isOverflowing = scrollWidth > clientWidth;
      if (isOverflowing) {
        e.preventDefault();
        slider.scrollLeft += e.deltaY;
      }
    };

    slider.addEventListener('wheel', handleWheel, { passive: false });
    return () => slider.removeEventListener('wheel', handleWheel);
  }, []);

  useEffect(() => {
    dispatch(getWishlist());
  }, [dispatch]);
  
  const formatPrice = (price) => {
    if (price === 'Price not set') return 'Free';
    if (price) {
      const priceRange = price.split("-");
      const firstPrice = parseFloat(priceRange[0].replace("£", ""));
      const secondPrice = priceRange[1] ? parseFloat(priceRange[1].replace("£", "")) : null;
      const isRange = priceRange.length === 2 && firstPrice !== secondPrice;
      const isUpTo = price.includes("Up to");
      const isSame = priceRange.length === 1 || firstPrice === secondPrice;

      if (isRange) return `From £${firstPrice}`;
      else if (isUpTo) return `${priceRange[0]}`;
      else if (isSame) return `£${firstPrice}`;
    }
    return "N/A";
  };

  return (
    <div className="w-full mb-6">
      <div
        ref={sliderRef}
        className={`${isPurchaseHistory
          ? 'grid gap-4 py-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
          : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 py-4'
          }`}
      >
        {loading
          ? Array(6).fill(0).map((_, index) => (
              <PlaceholderCard isPurchaseHistory={isPurchaseHistory} key={index} />
            ))
          : data.length > 0 ? (
            <>
              {data.map((EventData, index) => {
                const handleClick = () => {
                  if (isPurchaseHistory && onCardClick) {
                    onCardClick(EventData);
                  }
                };

                const isWishlist = wishlist?.some(item => item.event === EventData.event_id);

                return isPurchaseHistory ? (
                  <div
                    key={index}
                    onClick={handleClick}
                    className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all duration-200 flex flex-col"
                  >
                    <div
                      className="relative w-full h-32 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(${EventData?.thumbnail_path || 'https://via.placeholder.com/200x128.png?text=Event'})`,
                      }}
                    />
                    <div className="p-3 flex flex-col flex-grow">
                      <h4 className="font-semibold text-sm text-gray-800 line-clamp-2 mb-2" title={EventData.event_name}>
                        {truncateText(EventData.event_name, 8)}
                      </h4>
                      <div className="text-xs text-gray-500 space-y-1">
                        <p>Ref: {EventData.reference_id || 'N/A'}</p>
                        <p>Qty: {EventData.ticket_quantity || 'N/A'}</p>
                      </div>
                      <div className="mt-auto pt-2">
                        <p className="font-bold text-sm text-[#FF9000]">
                          {formatPrice(EventData.price_range)}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div key={index} className="group bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all duration-200 flex flex-col">
                    {/* Image Section */}
                    <div className="relative overflow-hidden">
                      <Link to={`/event/${EventData.event_id}`}>
                        <img
                          src={EventData?.thumbnail_path || 'https://via.placeholder.com/300x200?text=Event'}
                          alt={EventData.event_name}
                          className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-200"
                          loading="lazy"
                        />
                      </Link>
                      
                      {/* Wishlist Button */}
                      <div className="absolute top-2 right-2">
                        <FavoriteButton
                          inside={true}
                          eventId={EventData.event_id}
                          isWishlist={isWishlist}
                          className="w-8 h-8 rounded-full shadow-lg flex items-center justify-center"
                          buttonClassName={isWishlist 
                            ? "w-full h-full bg-[#FF8100] hover:bg-[#e6730e] transition-colors duration-200 rounded-full flex items-center justify-center" 
                            : "w-full h-full bg-white/90 hover:bg-white transition-colors duration-200 rounded-full flex items-center justify-center"
                          }
                          iconClassName={isWishlist 
                            ? "w-4 h-4 text-white" 
                            : "w-4 h-4 text-[#0056A6]"
                          }
                        />
                      </div>
                    </div>
                    
                    {/* Content Section */}
                    <div className="p-3 flex flex-col flex-grow">
                      {/* Title */}
                      <Link to={`/event/${EventData.event_id}`}>
                        <h3 className="font-semibold text-sm text-gray-800 line-clamp-2 mb-2 group-hover:text-[#0056A6] transition-colors">
                          {EventData.event_name}
                        </h3>
                      </Link>
                      
                      {/* Event Details */}
                      <div className="space-y-1 mb-3">
                        <div className="flex items-center text-xs text-gray-500">
                          <svg className="w-3 h-3 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                          <span className="truncate">
                            {new Date(EventData.start_date).toLocaleDateString('en-US', {
                              day: 'numeric',
                              month: 'short'
                            })}
                          </span>
                        </div>
                        
                        <div className="flex items-center text-xs text-gray-500">
                          <svg className="w-3 h-3 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                          </svg>
                          <span className="truncate">
                            {EventData.venue_name || 'Venue TBA'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Price and Button - Same Line Layout */}
                      <div className="mt-auto flex items-center justify-between gap-2">
                        <span className="font-bold text-sm text-[#FF9000] flex-shrink-0">
                          {formatPrice(EventData.price_range)}
                        </span>
                        
                        <Link 
                          to={`/event/${EventData.event_id}`}
                          className="bg-[#0056A6] hover:bg-[#004494] text-white text-xs font-medium py-1.5 px-3 rounded-md transition-colors duration-200 flex-shrink-0"
                        >
                          <span className="hidden sm:inline">Buy Tickets</span>
                          <span className="sm:hidden">Buy</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <p className="text-center col-span-full text-gray-500 py-8">No events available.</p>
          )}
      </div>
    </div>
  );
};

CardSlider.propTypes = {
  loading: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.object),
  isPurchaseHistory: PropTypes.bool,
  onCardClick: PropTypes.func,
};

export default CardSlider;
